import Box from 'ext-ui/components/Box/Box'
import { spacing } from 'ext-ui/style/spacing'
import ChevronIcon from 'ext-ui/svg/icon/ChevronIcon'
import React, { ReactElement } from 'react'
import CurrentResponsibilityIcon from 'src/feature/ReceiptRecovery/CurrentResponsibilityIcon'
import InterventionQueueIcon from 'src/feature/ReceiptRecovery/InterventionQueueIcon'
import { RECEIPT_WORK_GROUPS } from 'src/feature/ReceiptRecovery/receiptRecovery.fixtures'
import {
  ReceiptRecoveryAccountingPeriod,
  ReceiptRecoveryStats,
  ReceiptRecoveryTransactionState,
  ReceiptState,
} from 'src/feature/ReceiptRecovery/receiptRecovery.types'
import { useTranslate } from 'src/localization/Trns'

import * as style from './ReceiptRecoveryOverview.style'

interface ReceiptRecoveryOverviewProps {
  accountingPeriods: readonly ReceiptRecoveryAccountingPeriod[]
  onOpenTransactions: (state: ReceiptRecoveryTransactionState) => void
  onPeriodSelect: (periodId: string) => void
  receiptStats: ReceiptRecoveryStats
  selectedPeriodId: string
}

interface QueueSignalCopy {
  action?: string
  detail: string
  title: string
}

const ReceiptRecoveryOverview = ({
  accountingPeriods,
  onOpenTransactions,
  onPeriodSelect,
  receiptStats,
  selectedPeriodId,
}: ReceiptRecoveryOverviewProps): ReactElement => {
  const translate = useTranslate()

  const getStateLabel = (state: ReceiptState): string => {
    switch (state) {
      case 'needs-review':
        return translate('Needs Review')
      case 'waiting-on-spender':
        return translate('Waiting on Spender')
      case 'receipt-reconciled':
        return translate('Receipt Reconciled')
    }
  }

  const getQueueSignalCopy = (
    state: Exclude<ReceiptState, 'receipt-reconciled'>,
    count: number,
  ): QueueSignalCopy => {
    switch (state) {
      case 'needs-review':
        return {
          title: translate('${count} recovered receipts need your review', {
            count: String(count),
          }),
          detail: translate(
            'Human decision · the Agent found a candidate and cannot confirm it',
          ),
          action: translate('Review in Work →'),
        }
      case 'waiting-on-spender':
        return {
          title: translate('${count} receipts are with the spender', {
            count: String(count),
          }),
          detail: translate('Waiting on Spender · no receipt attached yet'),
          action: translate('Open waiting queue →'),
        }
    }
  }

  const receiptWorkGroups = RECEIPT_WORK_GROUPS.map((group) => ({
    ...group,
    total:
      group.state === 'needs-review'
        ? receiptStats.needsReview
        : group.state === 'waiting-on-spender'
          ? receiptStats.waitingOnSpender
          : receiptStats.receiptReconciled,
  }))
  const openCount = receiptWorkGroups
    .filter(({ state }) => state !== 'receipt-reconciled')
    .reduce((total, group) => total + group.total, 0)
  const needsReviewCount = receiptStats.needsReview
  const waitingOnSpenderCount = receiptStats.waitingOnSpender
  const reconciledCount = receiptStats.receiptReconciled

  return (
    <div css={style.overview}>
      <section aria-labelledby="current-responsibility-heading">
        <Box className="overviewCard responsibilityCard" padding={spacing(5)}>
          <div className="responsibilityHeader">
            <div>
              <h2 id="current-responsibility-heading">
                {translate('Current responsibility')}
              </h2>
              <p>{translate('Every open item is counted exactly once.')}</p>
            </div>
            <a className="viewAllWork" href="#work-queue-heading">
              {translate('View all work →')}
            </a>
          </div>
          <ul className="responsibilityGrid">
            {receiptWorkGroups.map((group) => {
              const tile = (
                <>
                  <div className="tileLabelRow">
                    <span>{getStateLabel(group.state)}</span>
                    <CurrentResponsibilityIcon state={group.state} />
                  </div>
                  <strong data-state={group.state}>{group.total}</strong>
                </>
              )

              return (
                <li key={group.state}>
                  <button
                    aria-label={translate('View ${state} transactions', {
                      state: getStateLabel(group.state),
                    })}
                    className="responsibilityTile responsibilityTileButton"
                    type="button"
                    onClick={() => onOpenTransactions(group.state)}
                  >
                    {tile}
                  </button>
                </li>
              )
            })}
          </ul>
          <p className="responsibilityEquation">
            {translate(
              '${openCount} open = ${needsReviewCount} Needs Review + ${waitingOnSpenderCount} Waiting on Spender. ${reconciledCount} Receipt Reconciled are complete and sit outside the open count.',
              {
                openCount: String(openCount),
                needsReviewCount: String(needsReviewCount),
                waitingOnSpenderCount: String(waitingOnSpenderCount),
                reconciledCount: String(reconciledCount),
              },
            )}
          </p>
        </Box>
      </section>

      <div className="overviewColumns">
        <section aria-labelledby="accounting-period-heading">
          <h2 className="sectionLabel" id="accounting-period-heading">
            {translate('ACCOUNTING PERIOD')}
          </h2>
          <Box noPadding className="overviewCard periodCard">
            <ul>
              {accountingPeriods.map((period) => (
                <li
                  className="periodRow"
                  data-selected={period.id === selectedPeriodId}
                  key={period.id}
                >
                  <button
                    aria-current={
                      period.id === selectedPeriodId ? 'true' : undefined
                    }
                    className="periodButton"
                    type="button"
                    onClick={() => onPeriodSelect(period.id)}
                  >
                    <div>
                      <strong>{period.label}</strong>
                      <span>
                        {period.id === selectedPeriodId
                          ? translate('In view · ${count} open', {
                              count: String(openCount),
                            })
                          : period.summary
                            ? period.summary
                            : ''}
                      </span>
                    </div>
                    {period.id !== selectedPeriodId && (
                      <ChevronIcon
                        className="periodChevron"
                        direction="right"
                        aria-hidden="true"
                        focusable="false"
                        role="presentation"
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </Box>
        </section>

        <section aria-labelledby="work-queue-heading">
          <h2 className="sectionLabel" id="work-queue-heading">
            {translate('WORK QUEUE')}
          </h2>
          <Box noPadding className="overviewCard queueCard">
            <div className="queueHeader">
              <h3>{translate('What needs intervention')}</h3>
              <p>
                {translate(
                  'Agent, human, and spender responsibility stay distinct',
                )}
              </p>
            </div>
            <ul>
              {receiptWorkGroups
                .filter(({ state }) => state !== 'receipt-reconciled')
                .map((group) => {
                  const state = group.state as Exclude<
                    ReceiptState,
                    'receipt-reconciled'
                  >
                  const copy = getQueueSignalCopy(state, group.total)

                  return (
                    <li className="queueSignal" data-state={state} key={state}>
                      <div className="queueIcon">
                        <InterventionQueueIcon state={state} />
                      </div>
                      <div className="queueCopy">
                        <strong>{copy.title}</strong>
                        <span>{copy.detail}</span>
                        {copy.action && (
                          <button
                            className="linkButton"
                            type="button"
                            onClick={() => onOpenTransactions(state)}
                          >
                            {copy.action}
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
            </ul>
          </Box>
        </section>
      </div>
    </div>
  )
}

export default ReceiptRecoveryOverview
